import { TextInput } from '@mgmresorts/mgm-ui';
import { navigate } from 'hookrouter';
import qs from 'query-string';
import React, { useCallback, useMemo, useState, useContext, useEffect, useRef, Suspense } from 'react';
import { ShellAppHeaderProps } from 'web_mfe_shell/ShellAppHeader';

import { KNOWN_ERROR_MESSAGES } from '../../api/constants';
import { checkUser } from '../../api/fetch/signInOrJoin';
import { Alert } from '../../components/ui/Alert';
import { Container } from '../../components/ui/Container';
import { Logo } from '../../components/ui/Logo';
import { MGMRewardsPromoBanner } from '../../components/ui/MGMRewardsPromoBanner';
import { PageLayout } from '../../components/ui/PageLayout';
import { useContent } from '../../hooks/useContent';
import { useDerivedPropertySlug } from '../../hooks/useDerivedPropertySlug';
import { useFieldValidation } from '../../hooks/useFieldValidation';
import { useInput } from '../../hooks/useInput';
import { useMobile } from '../../hooks/useMediaQuery';
import { EventAttributes } from '../../utils/analytics/types';
import { useEvent } from '../../utils/analytics/useEvent';
import { usePageLoadEvent } from '../../utils/analytics/usePageLoadEvent';
import { ClientId, MaxLengths } from '../../utils/constants';
import { getFeaturesByClientId } from '../../utils/getFeaturesByClientId';
import { getKnownErrorMessageByClientId } from '../../utils/getKnownErrorMessage';
import { PersistentParametersContext } from '../../utils/persistParameterContext';
import { UserAccountContext } from '../../utils/userAccountContext';
import { emailValidator } from '../../utils/validators';
import { StyledLegalText } from '../SignIn/styles';

import { StyledButton, StyledHeading, StyledSubTitle, StyledFooterContainer } from './styles';

const ShellGlobalFooter = React.lazy(() => import('web_mfe_shell/GlobalFooter'));
const ShellAppHeader = React.lazy(() => import('web_mfe_shell/ShellAppHeader'));

export function SignInOrJoin() {
  const isMobile = useMobile();
  const trackEvent = useEvent();
  usePageLoadEvent('identity', 'home');
  const [error, setError] = useState<null | string>(null);
  const [isLoading, setIsLoading] = useState(false);
  const startEmailEntry = useRef(false);
  const { validateField, invalidFields, emptyFields } = useFieldValidation([emailValidator]);
  const { getContentByPath } = useContent();
  const { setUserEmail } = useContext(UserAccountContext);
  const queryParams = qs.parse(window.location.search);
  const { params } = useContext(PersistentParametersContext);
  const { email, client_id, redirect_uri } = queryParams;
  const { isAllowOnlySignIn, isDisplayMGMRewardsPromoBanner } = getFeaturesByClientId(
    (client_id || params?.client_id) as ClientId,
  );
  const hasInternalRedirect = ['mgmresorts.com', 'devtest.vegas'].some(domain => document?.referrer.includes(domain));
  let emailId = '';
  if (email) {
    emailId = email as string;
  }
  const [analyticsFieldErrors, setAnalyticsFieldErrors] = useState<string[]>([]);

  const { bind: bindUsername, value: login } = useInput(
    emailId,
    'email',
    (field, value) => {
      validateField(field, value);

      if (field === 'email' && value?.length === 1 && !startEmailEntry.current) {
        startEmailEntry.current = true;
        trackEvent('identity – start email entry', 'start email entry', 'startemailentry', {}, true);
      }
    },
    validateField,
  );
  const pageTitle: string = isAllowOnlySignIn
    ? getContentByPath('signInOrJoin.secondaryTitle')
    : getContentByPath('signInOrJoin.title');
  const subTitle: string = getContentByPath('signInOrJoin.subTitle');

  const legalText: string | undefined = getContentByPath(`signIn.legalText.${client_id}`);
  const property = useDerivedPropertySlug();

  useEffect(() => {
    if (invalidFields.length > 0 || emptyFields.length > 0) {
      const eventAttributes = {} as EventAttributes;
      if (
        (invalidFields.includes('email') || emptyFields.includes('email')) &&
        !analyticsFieldErrors.includes('email')
      ) {
        eventAttributes.errorField = 'Email';
        eventAttributes.errorDisplayed = getContentByPath('signIn.fields.email.error');
      }
    }

    invalidFields.length > 0 && setAnalyticsFieldErrors(invalidFields);
    emptyFields.length > 0 && setAnalyticsFieldErrors(emptyFields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidFields, invalidFields.length, emptyFields, emptyFields.length]);

  const processSignInOrJoin = useCallback(
    async username => {
      try {
        const accountTypePath = {
          'sign-in': 'signin',
          'sign-up': 'signup',
          activate: 'activate',
          reset_password: 'forgot-password',
        };
        const trackAccountType = {
          'sign-up': 'JOIN',
        };
        setIsLoading(true);
        const { email, accountType } = await checkUser(username);
        if (!accountType) throw Error(KNOWN_ERROR_MESSAGES['invalid_token']);
        const userAccountType: keyof typeof accountTypePath = accountType?.toLowerCase();
        setUserEmail(email);

        await trackEvent(
          'identity – next - path assignment click',
          'next',
          'nextpathclick',
          { pathAssigned: trackAccountType[userAccountType as keyof typeof trackAccountType] || accountType },
          true,
        );

        if (isAllowOnlySignIn && userAccountType !== 'sign-in') {
          let errorMessage = getKnownErrorMessageByClientId(client_id as ClientId);
          userAccountType === 'reset_password' &&
            (errorMessage = getContentByPath(`signIn.message.locked_out_${client_id}`) || errorMessage);

          throw Error(errorMessage.replaceAll('{REDIRECT_URI}', redirect_uri as string));
        } else {
          navigate(`/${accountTypePath[userAccountType as keyof typeof accountTypePath]}`, true);
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [setUserEmail, trackEvent, isAllowOnlySignIn, client_id, getContentByPath, redirect_uri],
  );

  const canRequestSignInOrJoin = useMemo(
    () => !invalidFields.length && !emptyFields.length && login && !isLoading,
    [invalidFields.length, isLoading, login, emptyFields.length],
  );

  const renderAlerts = () => (
    <>
      {error && <Alert text={error} data-testid='sign-in-or-join-error' />}
      {queryParams.message && getContentByPath(`signIn.message.${queryParams.message}`) && (
        <Alert type='info' text={getContentByPath(`signIn.message.${queryParams.message}`)} />
      )}
      {!queryParams.message && params.partner && getContentByPath(`signIn.message.partner.${params.partner}`) && (
        <Alert type='info' text={getContentByPath(`signIn.message.partner.${params.partner}`)} />
      )}
    </>
  );

  const renderForm = () => (
    <div>
      <TextInput
        variant='default'
        label={getContentByPath('signIn.fields.email.title')}
        id='email'
        testId='email'
        disabled={isLoading || !!emailId}
        error={invalidFields.includes('email') || emptyFields.includes('email')}
        hint={
          emptyFields.includes('email')
            ? getContentByPath('signIn.fields.email.error')
            : invalidFields.includes('email')
            ? getContentByPath('signIn.fields.email.invalid')
            : ''
        }
        {...bindUsername}
        maxLength={MaxLengths.Email}
        type='email'
        autoComplete='email'
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            validateField('email', login);
            if (!invalidFields.length && !emptyFields.length) {
              processSignInOrJoin(login);
            }
          }
        }}
      />
      {!!legalText && (
        <StyledLegalText variant='body-regular-m' testId='legal-text'>
          {legalText}
        </StyledLegalText>
      )}
      <StyledButton
        $hasPromoBanner={isDisplayMGMRewardsPromoBanner}
        label={getContentByPath('signInOrJoin.next')}
        onClick={e => {
          e?.preventDefault();
          processSignInOrJoin(login);
        }}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            processSignInOrJoin(login);
          }
        }}
        disabled={!canRequestSignInOrJoin}
        testId='sign-in-or-join'
        loading={isLoading}
        shape='square'
        size='large'
        variant='primary'
        fullWidth
        type='submit'
      />
    </div>
  );

  const renderSharedContent = () => (
    <>
      {(!isDisplayMGMRewardsPromoBanner || (isDisplayMGMRewardsPromoBanner && !isMobile)) && <Logo />}
      <StyledHeading
        $hasPromoBanner={isDisplayMGMRewardsPromoBanner}
        variant='heading-medium-3'
        as='h1'
        testId='page-title'
      >
        {pageTitle}
      </StyledHeading>

      {renderAlerts()}

      {isDisplayMGMRewardsPromoBanner && (
        <StyledSubTitle variant='body-regular-m' as='p' testId='sign-in-or-join-subtitle'>
          {subTitle}
        </StyledSubTitle>
      )}
      {renderForm()}
    </>
  );

  const desktopConfig = useMemo<ShellAppHeaderProps['desktopConfig']>(
    () => ({
      enableCartIcon: true,
      enableExploreResorts: false,
      enableGlowAnimation: false,
      enableSearch: false,
      enableSecondaryNav: false,
      enableSignIn: false,
      enableSiteLogo: true,
    }),
    [],
  );

  const mobileConfig = useMemo<ShellAppHeaderProps['mobileConfig']>(
    () => ({
      displayMobileBackButton: hasInternalRedirect,
      enableCartIcon: true,
      enableSecondaryNav: false,
      enableSidebarMenus: false,
      enableSignIn: false,
      enableSiteLogo: true,
      enableMenu: false,
      onMobileBackButtonClick: () => {
        window.history.back();
      },
    }),
    [hasInternalRedirect],
  );

  return (
    <>
      <div>
        {isDisplayMGMRewardsPromoBanner && (
          <Suspense fallback={<div>Loading...</div>}>
            <ShellAppHeader
              property={property}
              desktopConfig={desktopConfig}
              mobileConfig={mobileConfig}
              cartVariant={'icon'}
            />
          </Suspense>
        )}
        <PageLayout testId='route-/' title={pageTitle} description={getContentByPath('signInOrJoin.description')}>
          <Container
            height='auto'
            isDisplayMGMRewardsPromoBanner={isDisplayMGMRewardsPromoBanner}
            flexDirection={isDisplayMGMRewardsPromoBanner ? 'row' : 'column'}
            gap='40px'
          >
            <Container height='auto' flexDirection='column'>
              {renderSharedContent()}
            </Container>
            {isDisplayMGMRewardsPromoBanner && (
              <Container height='auto' flexDirection='column'>
                <MGMRewardsPromoBanner />
              </Container>
            )}
          </Container>
        </PageLayout>
        {isDisplayMGMRewardsPromoBanner && (
          <Suspense fallback={<div>Loading...</div>}>
            <StyledFooterContainer $isMobile={isMobile}>
              <ShellGlobalFooter property={property} footerType='text' />
            </StyledFooterContainer>
          </Suspense>
        )}
      </div>
    </>
  );
}

SignInOrJoin.displayName = 'SignInOrJoin';
